import { useCallback, useEffect, useRef, useState } from "react";
import { useLive } from "../lib/live";
import { Maximize2, Minimize2, Volume2, VolumeX, RefreshCw, Check, CheckCheck, RotateCcw, Clock3, User, Flame } from "lucide-react";
import "./kitchen.css";

const POLL_MS = 5000;
const NEXT = { new: "ready", ready: "served", served: "new" };

function useKitchenAudio() {
  const ctxRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const enable = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!ctxRef.current) ctxRef.current = new Ctx();
    ctxRef.current.resume();
    setEnabled(true);
  };
  const play = useCallback((notes, type = "sine") => {
    const ctx = ctxRef.current;
    if (!enabled || !ctx) return;
    notes.forEach(([freq, start, dur]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = type; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur + 0.05);
    });
  }, [enabled]);
  const chime = useCallback(() => play([[880, 0, 0.25], [1320, 0.18, 0.4]]), [play]);
  const beep = useCallback(() => play([[620, 0, 0.18], [620, 0.28, 0.18], [620, 0.56, 0.18]], "square"), [play]);
  return { enabled, enable, disable: () => setEnabled(false), chime, beep };
}

function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m` : `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const CHG = { increase: "ADD", reduce: "REDUCE", cancel: "CANCEL", note: "NOTE" };

function ModNotes({ mods }) {
  if (!mods?.length) return null;
  return (
    <div className="kds-mods" data-testid="kds-mod-list">
      {mods.slice(-3).map((m, i) => (
        <p key={i}><b>{CHG[m.change]}</b> {m.item_name} {m.old_qty}→{m.new_qty}{m.reason ? ` · ${m.reason}` : ""} <span>— {m.by_name}</span></p>
      ))}
    </div>
  );
}

function KotCard({ kot, now, alertMin, onKot, onItem }) {
  const elapsed = now - new Date(kot.printed_at).getTime();
  const overdue = kot.status === "new" && elapsed >= alertMin * 60000;
  return (
    <article className={`kds-card st-${kot.status} ${overdue ? "overdue" : ""}`} data-testid={`kds-card-${kot.id}`}>
      <header>
        <div>
          <span className="kds-kot-no" data-testid={`kds-kot-no-${kot.id}`}>{kot.kot_no}</span>
          {kot.type === "modification" && <span className="kds-mod-badge" data-testid={`kds-mod-badge-${kot.id}`}>MODIFIED · ref {kot.ref_kot_no}</span>}
          {kot.type !== "modification" && kot.modified && <span className="kds-mod-badge soft" data-testid={`kds-modified-${kot.id}`}>EDITED ×{kot.modifications?.length || 1}</span>}
          <h3 data-testid={`kds-table-${kot.id}`}>{kot.table_label || "Takeaway"}</h3>
          {kot.waiter_name && <small className="kds-waiter" data-testid={`kds-waiter-${kot.id}`}><User size={11} /> {kot.waiter_name}</small>}
        </div>
        <div className="kds-timer" data-testid={`kds-timer-${kot.id}`}>
          {overdue ? <Flame size={14} /> : <Clock3 size={14} />}
          <b>{fmtElapsed(elapsed)}</b>
          <span className={`kds-status ${kot.status}`} data-testid={`kds-status-${kot.id}`}>{overdue ? "LATE" : kot.status.toUpperCase()}</span>
        </div>
      </header>
      <ul>
        {kot.items.map((it, i) => (
          <li key={i}>
            <button className={`kds-item ${it.status} ${it.change ? "chg-" + it.change : ""}`} disabled={it.status === "cancelled"} onClick={() => onItem(kot.id, it.item_id, NEXT[it.status] || "ready")} data-testid={`kds-item-${kot.id}-${i}`}>
              <i className="kds-qty">{it.change === "increase" ? `+${it.qty}` : it.change === "cancel" ? "✕" : `${it.qty}×`}</i>
              <span>{it.change && <strong className="kds-chg">{CHG[it.change]}{it.change === "reduce" ? ` to ${it.new_qty} (was ${it.old_qty})` : it.change === "cancel" ? ` (was ${it.old_qty})` : ""}</strong>}{it.name}{it.notes && <em>{it.notes}</em>}{it.reason && <em>Reason: {it.reason}</em>}</span>
              <small>{it.status}</small>
            </button>
          </li>
        ))}
      </ul>
      <ModNotes mods={kot.modifications} />
      {kot.modified_by && <p className="kds-by">by {kot.modified_by}</p>}
      <footer>
        {kot.status === "served" ? (
          <button className="kds-act" onClick={() => onKot(kot.id, "new")} data-testid={`kds-recall-${kot.id}`}><RotateCcw size={14} /> Recall</button>
        ) : (
          <>
            {kot.status === "new" && <button className="kds-act ready" onClick={() => onKot(kot.id, "ready")} data-testid={`kds-ready-${kot.id}`}><Check size={14} /> All ready</button>}
            <button className="kds-act served" onClick={() => onKot(kot.id, "served")} data-testid={`kds-served-${kot.id}`}><CheckCheck size={14} /> Served</button>
          </>
        )}
      </footer>
    </article>
  );
}

export function KdsBoard({ title, subtitle, fetchBoard, setKot, setItem, extra, onError }) {
  const [board, setBoard] = useState(null);
  const [filter, setFilter] = useState("active");
  const [now, setNow] = useState(Date.now());
  const [offset, setOffset] = useState(0);
  const [full, setFull] = useState(false);
  const [err, setErr] = useState("");
  const seen = useRef(null);
  const rootRef = useRef(null);
  const audio = useKitchenAudio();
  const { chime, beep } = audio;

  const load = useCallback(async () => {
    try {
      const data = await fetchBoard(filter === "served");
      setOffset(new Date(data.server_time).getTime() - Date.now());
      setBoard(data); setErr("");
      const ids = new Set(data.kots.map((k) => k.id));
      if (seen.current && data.kots.some((k) => !seen.current.has(k.id) && k.status === "new")) chime();
      seen.current = new Set([...(seen.current || []), ...ids]);
    } catch (e) { setErr(onError(e)); }
  }, [fetchBoard, filter, chime, onError]);

  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  useLive(() => load());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { const h = () => setFull(!!document.fullscreenElement); document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h); }, []);

  const alertMin = board?.alert_minutes || 20;
  const serverNow = now + offset;
  const kots = (board?.kots || []).filter((k) => (filter === "served" ? k.status === "served" : k.status !== "served"));
  const overdueCount = kots.filter((k) => k.status === "new" && serverNow - new Date(k.printed_at).getTime() >= alertMin * 60000).length;

  useEffect(() => { if (!overdueCount) return; beep(); const t = setInterval(beep, 30000); return () => clearInterval(t); }, [overdueCount, beep]);

  const act = async (fn) => { try { await fn(); await load(); } catch (e) { setErr(onError(e)); } };
  const toggleFull = () => (document.fullscreenElement ? document.exitFullscreen() : rootRef.current?.requestFullscreen?.());
  const counts = { new: 0, ready: 0 };
  (board?.kots || []).forEach((k) => { if (counts[k.status] !== undefined) counts[k.status] += 1; });

  return (
    <section ref={rootRef} className={`kds-root ${full ? "is-full" : ""}`} data-testid="kds-board">
      <div className="kds-top">
        <div>
          <span className="overline">LIVE KITCHEN · AUTO-REFRESH {POLL_MS / 1000}s</span>
          <h1 data-testid="kds-title">{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="kds-stats">
          <div data-testid="kds-count-new"><b>{counts.new}</b><span>New</span></div>
          <div data-testid="kds-count-ready"><b>{counts.ready}</b><span>Ready</span></div>
          <div className={overdueCount ? "late" : ""} data-testid="kds-count-late"><b>{overdueCount}</b><span>Late &gt;{alertMin}m</span></div>
        </div>
      </div>
      <div className="kds-toolbar">
        <div className="kds-tabs">
          <button className={filter === "active" ? "on" : ""} onClick={() => setFilter("active")} data-testid="kds-filter-active">Active</button>
          <button className={filter === "served" ? "on" : ""} onClick={() => setFilter("served")} data-testid="kds-filter-served">Served (12h)</button>
        </div>
        <div className="kds-tools">
          {extra}
          <button onClick={audio.enabled ? audio.disable : audio.enable} className={audio.enabled ? "on" : "warn"} data-testid="kds-sound-toggle">
            {audio.enabled ? <Volume2 size={15} /> : <VolumeX size={15} />} {audio.enabled ? "Sound on" : "Tap to enable sound"}
          </button>
          <button onClick={load} data-testid="kds-refresh"><RefreshCw size={15} /></button>
          <button onClick={toggleFull} data-testid="kds-fullscreen">{full ? <Minimize2 size={15} /> : <Maximize2 size={15} />} {full ? "Exit" : "Full screen"}</button>
        </div>
      </div>
      {err && <div className="kds-error" data-testid="kds-error">{err}</div>}
      {board && !kots.length && <div className="kds-empty" data-testid="kds-empty">{filter === "served" ? "No served KOTs in the last 12 hours." : "All caught up — no pending KOTs."}</div>}
      <div className="kds-grid">
        {kots.map((k) => (
          <KotCard key={k.id} kot={k} now={serverNow} alertMin={alertMin}
            onKot={(id, st) => act(() => setKot(id, st))} onItem={(id, i, st) => act(() => setItem(id, i, st))} />
        ))}
      </div>
    </section>
  );
}
